import { useParams, Link } from 'react-router-dom';
import { categories, shows, breadcrumbMap } from '@/mocks/shows';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ShowCard from '@/components/base/ShowCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-foreground-950 mb-3">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryShows = shows.filter((s) => s.categorySlug === slug);

  // Schema
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `屁屁影院 - ${category.name}`,
        description: `${category.name}全集免费在线观看，高清流畅不缓冲，剧集完整同步更新。屁屁影院为您提供最新最全的${category.name}资源。`,
        url: `https://pipicinema.com/category/${slug}`,
        isPartOf: { '@type': 'WebSite', name: '屁屁影院', url: 'https://pipicinema.com' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://pipicinema.com' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://pipicinema.com/category/${slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      <div className="pt-20 md:pt-24 pb-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />

          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-xl">
              <i className={`${category.icon} text-background-50 text-lg`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">{category.name}</h1>
              <p className="text-sm text-foreground-500 mt-0.5">
                共 {categoryShows.length} 部作品
              </p>
            </div>
          </div>

          {/* Sub-nav */}
          <div className="flex items-center flex-wrap gap-2 mb-8">
            <Link
              to="/"
              className="px-4 py-1.5 bg-background-100 text-foreground-600 rounded-full text-sm hover:bg-secondary-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              全部
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  c.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>

          {/* Content Grid */}
          {categoryShows.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categoryShows.map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-sm">暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}